import { Injectable, Injector, LOCALE_ID, inject } from '@angular/core';

import { from, map, of, tap } from 'rxjs';
import { DataService } from './data-service';
import { environment } from '../environments/environment';

export const getLanguage = (injector: Injector) => (injector.get(LOCALE_ID) ?? 'en').split('-')[0];

@Injectable({
    providedIn: 'root',
})
export class AppService {
    private readonly injector = inject(Injector);
    public readonly dataService = inject(DataService);
    getTags = (parentPath = '/'): Promise<any[]> => {
        return this.dataService.get<any>(`tag?parentPath=${parentPath}&select=_id,name,translations`).then((res) => res.data ?? []);
    };

    slides = () =>
        this.dataService
            .get<any>('/slideshow?sort_by=order&active=true')
            .then((res) => res.data ?? [])
            .then((slides) =>
                slides.map((s) => {
                    const bgImage = s.bgImage?.[0];
                    const video = s.video?.[0];
                    const r = {
                        ...s,
                        bgImage,
                        video,
                        imgSrc: bgImage?.path,
                        videoSrc: video ? environment.base_url + '/' + video.path : undefined,
                    };
                    return r;
                })
            );
    private _artBoardsTypes: any[] = undefined;
    get artBoardsTypesList() {
        return this._artBoardsTypes;
    }
    artBoardsTypes = (this._artBoardsTypes ? of(this._artBoardsTypes) : from(this.getTags('/ArtBoardsTypes'))).pipe(
        tap((types) => (this._artBoardsTypes = types)),
        map((types) => {
            return types.map((t) => {
                const language = getLanguage(this.injector);
                return {
                    ...t,
                    ...(t.translations?.[language] ?? {}),
                };
            });
        })
    );

    getBlogs() {
        const language = getLanguage(this.injector);
        return this.dataService
            .get<any>(`/blogs?lang={in}${language}&select=lang,title,date,slug,description,authors,cardImage,translations`)
            .then((blogs) => blogs.data ?? [])
            .then((blogs) =>
                blogs.map((b) => {
                    return {
                        ...b,
                        ...(b.translations?.[language] ?? {}),
                        url: `/art-tales/${b.slug}`,
                        showMoreBtn: false,
                    };
                })
            );
    }
    async getBlog(slug: string) {
        const lang = getLanguage(this.injector);
        const blog = await this.dataService.get<any>(`/blogs?slug=${slug}`).then((res) => res.data?.[0]);
        const meta = {
            ...blog.seo,
            ...blog.translations?.[lang]?.seo,
        };

        const imagePath = blog.cardImage?.[0]?.path || 'category/paint.jpg';

        meta['content-language'] = meta['content-language'] || lang;
        meta.type = meta.type || 'article';

        meta.image = meta.image || imagePath; // ogImage
        meta.og ??= {};
        meta.og['og:image'] = meta.og?.['og:image'] || meta.image;

        meta.twitter ??= {};
        meta.twitter['twitter:image'] = meta.twitter?.['twitter:image'] || meta.image;
        meta.twitter['twitter:card'] = meta.twitter?.['twitter:card'] || 'summary_large_image';

        meta.title ??= meta.title || blog.title || blog.slug;
        meta.twitter['twitter:title'] = meta.twitter?.['twitter:title'] || meta.title;
        meta.twitter['twitter:description'] = meta.twitter?.['twitter:description'] || meta.description;
        meta.og['og:title'] = meta.og?.['og:title'] || meta.title;
        meta.og['og:description'] = meta.og?.['og:description'] || meta.description;

        meta.schema ??= {};
        meta.schema['blogPosting'] = {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: blog.title,
            author: {
                '@type': 'Person',
                name: blog.authors.map((a) => a.name).join(', '),
            },
            publisher: {
                '@type': 'Organization',
                name: 'Idris Esen Art',
                logo: {
                    '@type': 'ImageObject',
                    url: environment.base_url + `/${lang}/idris-esen-logo.png`,
                },
            },
            datePublished: blog.date,
            dateModified: blog.date,
            mainEntityOfPage: environment.base_url + `/${lang}/art-tales/${blog.slug}`,
            description: blog.description || meta.description,
            articleBody: blog.body,
            image: environment.base_url + `/` + meta.image,
            url: environment.base_url + `/${lang}/art-tales/${blog.slug}`,
        };

        return {
            ...blog,
            ...(blog.translations?.[lang] ?? {}),
            meta,
        };
    }

    getHomePageGalleryByCategory(categoryId: string): Promise<any[]> {
        const language = getLanguage(this.injector);
        return this.dataService
            .get<any>(`/artBoards?category={eq}${categoryId}&useInHomePage={eq}true&page=${1}&per_page=${6}&select=title,slug,category,date,largeImage`)
            .then((res) => res.data ?? []);
    }

    getHomePageGallery(categories: string[]): Promise<any[]> {
        return Promise.allSettled(categories.map((category) => this.getHomePageGalleryByCategory(category))).then((res) =>
            res
                .filter((r) => r.status === 'fulfilled')
                .map((r) => r['value'])
                .flat()
        );
    }

    async getGalleryFilter(collection: string) {
        return this.getTags(`/${collection}`);
    }
}
